import React from 'react';
import { SlSidebar, SlSidebarSectionContainer, SlSidebarTitle } from 'src/components/BeaconV2Components/SlSidebar';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { Box } from '@mui/system';
import { hexToRgba, useStacklineTheme } from '@stackline/ui';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import {
  FILTER_OPERATION,
  useSavedSearchBuilder
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/hooks/useSavedSearchBuilder';
import { BeaconProSidebarFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/constants';
import { useAppSelector, useHistory, useSnackbar } from 'src/utils/Hooks';
import { useDispatch } from 'react-redux';
import { ENTITIES } from 'src/utils/entityDefinitions';
import { AppName } from 'sl-api-connector';
import { updateSavedSearch as updateSegment } from 'src/store/modules/segments/operations';
import { addPersistentQueryParams } from 'src/utils/browser';
import { SegmentCreationSnackbar } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentCreationSnackbar';
import { AxiosResponse } from 'axios';
import { SegmentBuilderBulkUploadModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsV2/SegmentBuilderBulkUploadModal';
import LegacySidebarFilter from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsV2/LegacySidebarFilter';
import LegacyCategorySidebarFilter from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsV2/LegacyCategorySidebarFilter';
import LegacySubcategorySidebarFilter from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsV2/LegacySubcategorySidebarFilter';
import SidebarInput from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/SidebarInput';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';
import { shouldShowNewAtlas, getQueryParamValue } from 'src/utils/app';
import AtlasV2BrandSidebarFilter from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsV2/AtlasV2BrandSidebarFilter';
import _get from 'lodash/get';
import { mkExportAllSegments } from 'src/components/SegmentPowerTools/SegmentExport';
import BeaconV2BrandSidebarFilter from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsV2/BrandSidebarFilter';

/**
 * Filter group for the Segment Builder sidebar.
 */
const SegmentBuilderSidebarFilters = () => {
  return (
    <>
      <Box sx={{ marginTop: '31px' }}>
        <LegacySidebarFilter
          filterParameter={BeaconProSidebarFilters.KeywordPhrase.filterKey}
          transferableParameter={BeaconProSidebarFilters.SearchTermFuzzy.filterKey}
          title="Include - Phrase"
          sidebarInputProps={{ placeholder: 'Keyword phrase to include' }}
          includeClear
        />
      </Box>
      <Box sx={{ marginTop: '3px' }}>
        <LegacySidebarFilter
          filterParameter={BeaconProSidebarFilters.KeywordExact.filterKey}
          title="Include - Exact"
          sidebarInputProps={{ placeholder: 'Exact keyword to include' }}
          includeClear
        />
      </Box>
      <Box sx={{ marginTop: '3px' }}>
        <LegacySidebarFilter
          filterParameter={BeaconProSidebarFilters.ExcludedKeyword.filterKey}
          title="Exclude - Phrase"
          sidebarInputProps={{ placeholder: 'Keyword phrase to exclude' }}
          includeClear
        />
      </Box>
      <Box sx={{ marginTop: '3px' }}>
        <LegacySidebarFilter
          filterParameter={BeaconProSidebarFilters.ExcludedKeywordExact.filterKey}
          title="Exclude - Exact"
          sidebarInputProps={{ placeholder: 'Exact keyword to exclude' }}
          includeClear
        />
      </Box>

      {/* Brands */}
      {shouldShowNewAtlas() ? (
        <AtlasV2BrandSidebarFilter
          title="Brands"
          sidebarInputProps={{ placeholder: 'Refine by brands' }}
          includeClear
        />
      ) : (
        <Box sx={{ marginTop: '3px' }}>
          <BeaconV2BrandSidebarFilter
            title="Brands"
            sidebarInputProps={{ placeholder: 'Refine by brands' }}
            includeClear
            includeToggles
          />
        </Box>
      )}

      {/* Categories */}
      <Box sx={{ marginTop: '32px' }}>
        <LegacyCategorySidebarFilter
          title="Categories"
          sidebarInputProps={{ placeholder: 'Refine by categories' }}
          includeClear
          includeToggles
        />
      </Box>

      {/* Subcategories */}
      <Box sx={{ marginTop: '32px' }}>
        <LegacySubcategorySidebarFilter
          title="Subcategories"
          sidebarInputProps={{ placeholder: 'Refine by subcategories' }}
          includeClear
          includeToggles
        />
      </Box>
    </>
  );
};

/**
 * Memoized version of the SegmentBuilderSidebarFilters component to avoid unnecessary rerenders
 */
const MemoizedSegmentFilters = React.memo(SegmentBuilderSidebarFilters);

/**
 * V2 of the segment builder sidebar with updated styles, filters, and functionality.
 */
export const SegmentBuilderSidebar = () => {
  const theme = useStacklineTheme();
  const { showSnackbar } = useSnackbar();
  const allSegments = useAppSelector((state) => state.segments.savedSearchesById);
  const teamSegments = useAppSelector((state) => state.segments.teamSegments);
  const mySegments = useAppSelector((state) => state.segments.mySegments);
  const dispatch = useDispatch();
  const history = useHistory();
  const entityType = 'segment';
  const retailer = useAppSelector((state) => state.retailer);
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const entityIdBeingEdited = getQueryParamValue('id');
  const alreadySavedEntity = getQueryParamValue('dn');
  const app = useAppSelector((state) => state.app);
  const categories = useAppSelector((state) => state.categories);
  const subCategories = useAppSelector((state) => state.subCategories);

  const { formData, updateSavedSearch } = useSavedSearchBuilder();

  const [name, setName] = React.useState(formData?.dn || '');
  const [bulkUploadModalOpen, setBulkUploadModalOpen] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  // Export Criteria button
  const criteriaExport = () => {
    const filteredSegment = {
      mySegments: mySegments.filter(
        (ele) => _get(ele, ['id']) === entityIdBeingEdited || _get(ele, ['segment', 'dn']) === alreadySavedEntity
      ),
      mySearchTermLists: [],
      teamSegments: teamSegments.filter((ele) => _get(ele, ['segment', 'dn']) === name),
      teamSearchTermLists: []
    };

    const exportTheSegment = mkExportAllSegments(
      { segments: filteredSegment, categories, subCategories, retailer, app },
      `${name}-saved-search.csv`
    );

    exportTheSegment();
  };

  /**
   * Handles the saving of a segment using the legacy logic.
   */
  const handleSaveSegment = async () => {
    try {
      const segmentName = name.trim();
      if (segmentName) {
        setIsSaving(true);
        const savedSearchPayload = { ...formData, dn: segmentName };
        const entityDefinition = ENTITIES[AppName.Beacon][entityType];
        const lowerCaseDisplayName = (name || '').toLowerCase();

        const hasNameCollision = !!Array.from(allSegments.values()).find(
          (savedSearch) =>
            savedSearch.id !== formData.id && savedSearch.displayName.toLowerCase() === lowerCaseDisplayName
        );

        if (hasNameCollision) {
          showSnackbar({
            message: 'You must pick a unique name for this segment; a segment/BU with the same name already exists.',
            type: 'error'
          });
          setIsSaving(false);
          return false;
        }

        const response = (await dispatch(
          updateSegment(entityDefinition.type, savedSearchPayload, formData.id, null, true, {})
        )) as unknown as AxiosResponse;

        setIsSaving(false);
        history.push(
          `/${entityDefinition.type}/${response.data}?${addPersistentQueryParams(
            retailer,
            mainTimePeriod
          )}&tab=sales&subtab=retailSales`
        );
        showSnackbar({
          type: 'success',
          message: <SegmentCreationSnackbar isTopicBuilder={false} />
        });
      } else {
        return null;
      }
    } catch (error) {
      setIsSaving(false);
      showSnackbar({
        message: <GenericErrorMessage />,
        type: 'error'
      });
      console.error('Error saving segment', error);
    }

    return null;
  };

  return (
    <SlSidebar gap={0} sx={{ width: 'auto' }}>
      <SlSidebarSectionContainer gap={0}>
        <Text sx={{ marginBottom: '32px', marginTop: '54px' }} variant="h4">
          Filter
        </Text>
        <SlSidebarTitle>Name</SlSidebarTitle>
        <SidebarInput
          style={{ marginTop: '8px' }}
          onChange={(e) => setName(e.target.value)}
          value={name}
          placeholder="Name your segment"
        />
        <MemoizedSegmentFilters />

        <SegmentBuilderBulkUploadModal open={bulkUploadModalOpen} handleClose={() => setBulkUploadModalOpen(false)} />

        <Flex flexDirection="column" marginTop="64px" gap={16}>
          <SlButton disabled={!name.trim() || isSaving} onClick={handleSaveSegment} variant="contained">
            {isSaving ? 'Saving...' : 'Save Segment'}
          </SlButton>
          <SlButton
            onClick={() => setBulkUploadModalOpen(true)}
            variant="tertiary"
          >
            Bulk Upload
          </SlButton>
          {alreadySavedEntity && (
            <SlButton
              variant="accent"
              onClick={() => criteriaExport()}
            >
              Criteria Export
            </SlButton>
          )}
          <SlButton
            onClick={() => {
              updateSavedSearch({ key: null, newValue: null, operation: FILTER_OPERATION.Clear_All });
            }}
          >
            Clear Filters
          </SlButton>
        </Flex>
      </SlSidebarSectionContainer>
    </SlSidebar>
  );
};
